/**
 * ScenarioBuilder - describes the test applicant the package is generated for.
 *
 * Only the choices the server-side disclosure resolver actually reads are offered
 * (see DisclosurePackagePreviewService header): ownership type, who owns which
 * account (minors never sign), check order, overdraft election, payable-on-death
 * beneficiaries. Interest-bearing is shown, not chosen - it comes from the product
 * catalog and the products.interestBearingCodes setting. Debit card, e-statements
 * and online banking are NOT offered: no disclosure rule reads them today.
 */
import { useMemo, useState } from 'react';
import { Plus, Trash2, UserPlus } from 'lucide-react';

import type {
  CatalogProduct,
  OverdraftChoice,
  PersonRole,
  PreviewCatalog,
  Scenario,
  ScenarioBeneficiary,
  ScenarioProduct,
} from './packagePreviewApi';
import { blankProduct } from './scenario';

interface Props {
  catalog: PreviewCatalog;
  scenario: Scenario;
  onChange: (next: Scenario) => void;
}

const ROLE_LABEL: Record<PersonRole, string> = { primary: 'Primary owner', joint: 'Joint owner', custodian: 'Custodian' };

export function ScenarioBuilder({ catalog, scenario, onChange }: Props): JSX.Element {
  const [showOutOfPhase, setShowOutOfPhase] = useState(
    () => !catalog.ownershipTypes.find((t) => t.customerType === scenario.ownershipType)?.inPhase1Pilot
      && scenario.ownershipType !== 'Individual',
  );
  const [addCode, setAddCode] = useState('');
  const productByCode = useMemo(() => new Map(catalog.products.map((p) => [p.code, p])), [catalog.products]);

  const set = (patch: Partial<Scenario>): void => onChange({ ...scenario, ...patch });
  const setProduct = (i: number, patch: Partial<ScenarioProduct>): void =>
    set({ products: scenario.products.map((p, ix) => (ix === i ? { ...p, ...patch } : p)) });

  const phase1 = catalog.ownershipTypes.filter((t) => t.inPhase1Pilot);
  const outOfPhase = catalog.ownershipTypes.filter((t) => !t.inPhase1Pilot);
  const ownershipType = catalog.ownershipTypes.find((t) => t.customerType === scenario.ownershipType);

  const removePerson = (i: number): void => {
    // Re-map product owner indexes so they keep pointing at the same people.
    const people = scenario.people.filter((_, ix) => ix !== i);
    const products = scenario.products.map((p) => ({
      ...p,
      ownerIndexes: p.ownerIndexes.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)),
    }));
    set({ people, products });
  };

  const setRole = (i: number, role: PersonRole): void => {
    const people = scenario.people.map((p, ix) => {
      if (ix === i) return { ...p, role };
      // Exactly one primary owner: the old primary becomes a joint owner.
      if (role === 'primary' && p.role === 'primary') return { ...p, role: 'joint' as PersonRole };
      return p;
    });
    set({ people });
  };

  return (
    <div>
      <div className="pg-section">
        <h3 className="pg-section__title">Ownership type</h3>
        <div className="pg-seg" role="group" aria-label="Ownership type">
          {phase1.map((t) => (
            <button key={t.customerType} type="button" aria-pressed={scenario.ownershipType === t.customerType}
              onClick={() => set({ ownershipType: t.customerType })}>
              {t.displayName || t.customerType}
            </button>
          ))}
        </div>
        {outOfPhase.length > 0 && (
          <div className="pg-row" style={{ marginTop: 8 }}>
            <label className="pg-check">
              <input type="checkbox" checked={showOutOfPhase} onChange={(e) => setShowOutOfPhase(e.target.checked)} />
              Show types outside the Phase 1 pilot
            </label>
          </div>
        )}
        {showOutOfPhase && outOfPhase.length > 0 && (
          <div className="pg-row">
            <select className="pg-select" value={ownershipType?.inPhase1Pilot ? '' : scenario.ownershipType}
              onChange={(e) => e.target.value && set({ ownershipType: e.target.value })} aria-label="Ownership type outside Phase 1">
              <option value="">Choose a type outside Phase 1...</option>
              {outOfPhase.map((t) => (
                <option key={t.customerType} value={t.customerType}>
                  {t.displayName || t.customerType} ({t.status === 'UAT' ? 'Pilot' : t.status})
                </option>
              ))}
            </select>
          </div>
        )}
        {ownershipType && !ownershipType.inPhase1Pilot && (
          <p className="pg-hint"><span className="pill pill--warn">Outside Phase 1</span> The preview is approximate for this type.</p>
        )}
        <div className="pg-row" style={{ marginTop: 8 }}>
          <label className="pg-check">
            <input type="checkbox" checked={scenario.stampOwnershipOnCart}
              onChange={(e) => set({ stampOwnershipOnCart: e.target.checked })} />
            Also record the ownership type on the cart
          </label>
        </div>
        <p className="pg-hint">
          This adds the documents the entity rules require. The officer app does not record it on carts today, so leave it
          off to see what a real application gets.
        </p>
      </div>

      <div className="pg-section">
        <h3 className="pg-section__title">
          People
          <button type="button" className="btn btn--ghost btn--sm" disabled={scenario.people.length >= catalog.limits.maxPeople}
            onClick={() => set({ people: [...scenario.people, { role: 'joint', minor: false }] })}>
            <UserPlus size={14} /> Add person
          </button>
        </h3>
        {scenario.people.map((p, i) => (
          <div className="pg-row" key={i}>
            <span style={{ width: 18, fontSize: 12, color: 'var(--ink-500)' }}>{i + 1}</span>
            <select className="pg-select" value={p.role} onChange={(e) => setRole(i, e.target.value as PersonRole)} aria-label={`Person ${i + 1} role`}>
              {(Object.keys(ROLE_LABEL) as PersonRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <label className="pg-check">
              <input type="checkbox" checked={p.minor} onChange={(e) => set({ people: scenario.people.map((x, ix) => (ix === i ? { ...x, minor: e.target.checked } : x)) })} />
              Minor
            </label>
            <button type="button" className="btn btn--ghost btn--sm" aria-label={`Remove person ${i + 1}`}
              disabled={scenario.people.length <= 1} onClick={() => removePerson(i)}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <p className="pg-hint">Test people are named "... Test" with 666 tax ids. Minors never sign; a custodian signs for them.</p>
      </div>

      <div className="pg-section">
        <h3 className="pg-section__title">Products</h3>
        {scenario.products.length === 0 && <p className="pg-hint">Add at least one product.</p>}
        {scenario.products.map((sp, i) => {
          const prod = productByCode.get(sp.code);
          return prod ? (
            <ProductCard key={`${sp.code}-${i}`} product={prod} value={sp} people={scenario.people}
              ownershipType={scenario.ownershipType} odLimitTiers={catalog.odLimitTiers}
              maxBeneficiaries={Math.min(prod.maxBeneficiaries || catalog.limits.maxBeneficiaries, catalog.limits.maxBeneficiaries)}
              onChange={(patch) => setProduct(i, patch)}
              onRemove={() => set({ products: scenario.products.filter((_, ix) => ix !== i) })} />
          ) : (
            <div key={`${sp.code}-${i}`} className="banner banner--warn">
              Product {sp.code} is no longer in the catalog.{' '}
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => set({ products: scenario.products.filter((_, ix) => ix !== i) })}>Remove</button>
            </div>
          );
        })}
        <div className="pg-row">
          <select className="pg-select" value={addCode} onChange={(e) => setAddCode(e.target.value)} aria-label="Product to add" style={{ flex: 1, minWidth: 0 }}>
            <option value="">Choose a product...</option>
            {groupByCategory(catalog.products).map(([cat, list]) => (
              <optgroup key={cat} label={cat}>
                {list.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name} ({p.code}){p.customerTypes.length > 0 && !p.customerTypes.includes(scenario.ownershipType) ? ' - not offered to this ownership type' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <button type="button" className="btn btn--sm" disabled={!addCode || scenario.products.length >= catalog.limits.maxProducts}
            onClick={() => { set({ products: [...scenario.products, blankProduct(addCode)] }); setAddCode(''); }}>
            <Plus size={14} /> Add
          </button>
        </div>
      </div>

      <div className="pg-section">
        <h3 className="pg-section__title">Branch</h3>
        <select className="pg-select" value={scenario.branchCode} onChange={(e) => set({ branchCode: e.target.value })} aria-label="Branch">
          <option value="">First branch in the list</option>
          {catalog.branches.map((b) => <option key={b.code} value={b.code}>{b.code} - {b.name}</option>)}
        </select>
      </div>
    </div>
  );
}

function groupByCategory(products: ReadonlyArray<CatalogProduct>): Array<[string, CatalogProduct[]]> {
  const m = new Map<string, CatalogProduct[]>();
  for (const p of products) {
    const k = p.category || 'Other';
    m.set(k, [...(m.get(k) ?? []), p]);
  }
  return [...m.entries()];
}

interface ProductCardProps {
  product: CatalogProduct;
  value: ScenarioProduct;
  people: Scenario['people'];
  ownershipType: string;
  odLimitTiers: ReadonlyArray<number>;
  maxBeneficiaries: number;
  onChange: (patch: Partial<ScenarioProduct>) => void;
  onRemove: () => void;
}

function ProductCard({ product, value, people, ownershipType, odLimitTiers, maxBeneficiaries, onChange, onRemove }: ProductCardProps): JSX.Element {
  const owners = value.ownerIndexes.length > 0 ? value.ownerIndexes : people.map((_, i) => i);
  const toggleOwner = (i: number, on: boolean): void => {
    const cur = new Set(owners);
    if (on) cur.add(i); else cur.delete(i);
    const primary = people.findIndex((p) => p.role === 'primary');
    // Primary owner first (the server takes the first index as the primary owner).
    const next = [...cur].sort((a, b) => (a === primary ? -1 : b === primary ? 1 : a - b));
    onChange({ ownerIndexes: next.length === people.length ? [] : next });
  };
  const checksOffered = product.checks?.offered ?? false;
  const odOffered = product.overdraft?.offered ?? false;
  const notForType = product.customerTypes.length > 0 && !product.customerTypes.includes(ownershipType);

  const setBenef = (i: number, patch: Partial<ScenarioBeneficiary>): void =>
    onChange({ beneficiaries: value.beneficiaries.map((b, ix) => (ix === i ? { ...b, ...patch } : b)) });

  return (
    <div className="pg-product">
      <div className="pg-product__head">
        <span className="pg-product__name">{product.name}</span>
        <span className="pg-code">{product.code}</span>
        <span className="pill pill--muted">{product.category}</span>
        {product.interestBearing && <span className="pill pill--info" title="From the product catalog or the products.interestBearingCodes setting">Interest-bearing</span>}
        {notForType && <span className="pill pill--warn">Not offered to this ownership type</span>}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn--ghost btn--sm" aria-label={`Remove ${product.name}`} onClick={onRemove}><Trash2 size={14} /></button>
      </div>
      <div className="pg-product__grid">
        <span className="pg-label">Owners</span>
        <div className="pg-row">
          {people.map((p, i) => (
            <label key={i} className="pg-check">
              <input type="checkbox" checked={owners.includes(i)} disabled={p.role === 'primary'}
                onChange={(e) => toggleOwner(i, e.target.checked)} />
              {i + 1}{p.minor ? ' (minor)' : ''}
            </label>
          ))}
        </div>

        <span className="pg-label">Checks</span>
        <label className={`pg-check${checksOffered ? '' : ' pg-check--disabled'}`}>
          <input type="checkbox" checked={value.orderChecks && checksOffered} disabled={!checksOffered}
            onChange={(e) => onChange({ orderChecks: e.target.checked })} />
          {checksOffered ? 'Checks ordered' : 'Not offered on this product'}
        </label>

        <span className="pg-label">Overdraft</span>
        {odOffered ? (
          <div className="pg-row">
            <div className="pg-seg" role="group" aria-label="Overdraft coverage">
              {(['none', 'optOut', 'optIn'] as OverdraftChoice[]).map((o) => (
                <button key={o} type="button" aria-pressed={value.overdraft === o} onClick={() => onChange({ overdraft: o })}>
                  {o === 'none' ? 'Not asked' : o === 'optOut' ? 'Declined' : 'Elected'}
                </button>
              ))}
            </div>
            {value.overdraft === 'optIn' && (
              <select className="pg-select" value={value.odLimit} onChange={(e) => onChange({ odLimit: Number(e.target.value) })} aria-label="Overdraft limit">
                {odLimitTiers.map((t) => <option key={t} value={t}>${t}</option>)}
              </select>
            )}
          </div>
        ) : (
          <span className="pg-check pg-check--disabled">Not offered on this product</span>
        )}

        <span className="pg-label">Beneficiaries</span>
        {product.allowBeneficiaries ? (
          <div>
            {value.beneficiaries.map((b, i) => (
              <div className="pg-row" key={i}>
                <select className="pg-select" value={b.role} onChange={(e) => setBenef(i, { role: e.target.value as 'primary' | 'contingent' })} aria-label={`Beneficiary ${i + 1} tier`}>
                  <option value="primary">Primary</option>
                  <option value="contingent">Contingent</option>
                </select>
                <label className="pg-check">
                  <input type="checkbox" checked={b.minor} onChange={(e) => setBenef(i, { minor: e.target.checked })} /> Minor
                </label>
                <input className="pg-input pg-input--num" type="number" min={0} max={100} placeholder="equal %"
                  value={b.percentage ?? ''} aria-label={`Beneficiary ${i + 1} share`}
                  onChange={(e) => setBenef(i, { percentage: e.target.value === '' ? null : Number(e.target.value) })} />
                <button type="button" className="btn btn--ghost btn--sm" aria-label={`Remove beneficiary ${i + 1}`}
                  onClick={() => onChange({ beneficiaries: value.beneficiaries.filter((_, ix) => ix !== i) })}><Trash2 size={14} /></button>
              </div>
            ))}
            <button type="button" className="btn btn--ghost btn--sm" disabled={value.beneficiaries.length >= maxBeneficiaries}
              onClick={() => onChange({ beneficiaries: [...value.beneficiaries, { role: 'primary', minor: false }] })}>
              <Plus size={14} /> Add beneficiary
            </button>
          </div>
        ) : (
          <span className="pg-check pg-check--disabled">Not allowed on this product</span>
        )}
      </div>
    </div>
  );
}
